import { useCallback, useEffect, useState } from "react";
import { MessageCircle, ChevronLeft } from "lucide-react";

interface OtpNotificationProps {
  otpCode: string;
  otpSender: string;
  /** Localized "Your verification code is:" prefix */
  codeLabel?: string;
  /** Localized "Do not share this code with anyone." suffix */
  codeSuffix?: string;
  /** Localized "Back" button text */
  backLabel?: string;
  /** Localized bottom hint */
  backHint?: string;
  /** Localized "now" timestamp label */
  nowLabel?: string;
  /** Delay before showing the notification banner (ms) */
  delayMs?: number;
}

type OtpView = "hidden" | "banner" | "message" | "minimized";

export function OtpNotification({
  otpCode,
  otpSender,
  codeLabel = "Your verification code is:",
  codeSuffix = "Do not share this code with anyone.",
  backLabel = "Back",
  backHint = "Tap \"Back\" to return to the chat",
  nowLabel = "now",
  delayMs = 2500,
}: OtpNotificationProps) {
  const [view, setView] = useState<OtpView>("hidden");

  // Show banner after delay — resets when a new otpCode arrives (escalation scenario)
  useEffect(() => {
    setView("hidden");
    const timer = setTimeout(() => setView("banner"), delayMs);
    return () => clearTimeout(timer);
  }, [otpCode, delayMs]);

  // Auto-minimize banner after 8 seconds if not tapped
  useEffect(() => {
    if (view !== "banner") return;
    const timer = setTimeout(() => setView("minimized"), 8000);
    return () => clearTimeout(timer);
  }, [view]);

  const handleBannerTap = useCallback(() => {
    setView("message");
  }, []);

  const handleBack = useCallback(() => {
    setView("minimized");
  }, []);

  const codeMessage = `${codeLabel} ${otpCode}. ${codeSuffix}`;

  // Banner — iOS notification style, slides in from top
  if (view === "banner") {
    return (
      <div className="absolute left-2 right-2 top-2 z-40 animate-[slideDown_0.3s_ease-out]">
        <button
          type="button"
          onClick={handleBannerTap}
          className="w-full overflow-hidden rounded-2xl border border-white/30 bg-white/90 p-2.5 text-left shadow-lg backdrop-blur-xl transition-transform active:scale-[0.98] dark:border-white/10 dark:bg-[#2C2C2E]/90"
        >
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-emerald-400 to-emerald-500">
              <MessageCircle size={14} className="fill-white text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between">
                <span className="text-[12px] font-semibold text-[#1C1C1E] dark:text-[#F2F2F7]">
                  {otpSender}
                </span>
                <span className="text-[10px] text-[#8E8E93]">{nowLabel}</span>
              </div>
              <p className="truncate text-[11px] text-[#3C3C43]/70 dark:text-[#EBEBF5]/60">
                {codeLabel} {otpCode}...
              </p>
            </div>
          </div>
        </button>
      </div>
    );
  }

  // Full message view — overlays the chat
  if (view === "message") {
    return (
      <div className="absolute inset-0 z-40 flex flex-col bg-white dark:bg-[#1C1C1E]">
        {/* Header */}
        <div className="flex items-center gap-2 border-b border-black/10 px-3 py-2 dark:border-white/10">
          <button
            type="button"
            onClick={handleBack}
            className="flex items-center gap-0.5 text-[14px] font-medium text-blue-500 dark:text-blue-400"
          >
            <ChevronLeft size={18} />
            {backLabel}
          </button>
          <div className="flex-1 text-center">
            <span className="text-[14px] font-semibold text-[#1C1C1E] dark:text-[#F2F2F7]">
              {otpSender}
            </span>
          </div>
          <div className="w-12" /> {/* Spacer for centering */}
        </div>

        {/* Message */}
        <div className="flex-1 overflow-y-auto px-3 pt-4">
          <div className="flex flex-col items-center gap-1 pb-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#8E8E93]/20">
              <MessageCircle size={20} className="text-[#8E8E93]" />
            </div>
            <span className="text-[12px] text-[#8E8E93]">{otpSender}</span>
            <span className="text-[10px] text-[#8E8E93]">{nowLabel}</span>
          </div>

          <div className="self-start max-w-[85%] rounded-[18px] border border-black/5 bg-[#E9E9EB] px-3.5 py-2 dark:border-white/10 dark:bg-[#3A3A3C]">
            <p className="text-[14px] leading-snug text-[#1C1C1E] dark:text-[#F2F2F7]">
              {codeMessage}
            </p>
          </div>
        </div>

        {/* Bottom hint */}
        <div className="border-t border-black/10 px-4 py-3 text-center dark:border-white/10">
          <p className="text-[12px] text-[#8E8E93]">
            {backHint}
          </p>
        </div>
      </div>
    );
  }

  // Minimized pill — persistent, tappable to re-view the code
  if (view === "minimized") {
    return (
      <div className="absolute right-2 top-2 z-40 animate-[slideDown_0.2s_ease-out]">
        <button
          type="button"
          onClick={() => setView("message")}
          className="flex items-center gap-1.5 rounded-full border border-white/30 bg-white/90 px-3 py-1.5 shadow-md backdrop-blur-xl transition-transform active:scale-95 dark:border-white/10 dark:bg-[#2C2C2E]/90"
        >
          <div className="flex h-5 w-5 items-center justify-center rounded-full bg-gradient-to-br from-emerald-400 to-emerald-500">
            <MessageCircle size={10} className="fill-white text-white" />
          </div>
          <span className="text-[11px] font-medium text-[#1C1C1E] dark:text-[#F2F2F7]">
            {otpSender}
          </span>
          <span className="flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[9px] font-bold text-white">
            1
          </span>
        </button>
      </div>
    );
  }

  return null;
}
